import { yupResolver } from '@hookform/resolvers/yup';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import isEqual from 'lodash/isEqual';
import { mixed, object, string } from 'yup';

import { formatDay, IYearMonth } from '~/utils/date/date';
import { minutesToDuration } from '~/utils/date/duration';
import { ICeEntry } from '~/services/api/communityEngagement/types';
import { DateRangeValue } from '~/ui/components/inputs/DateRangePicker/types';
import { isCeDuration } from '~/ui/configs/form/ceDuration';
import {
  CE_COMMENT_TOO_LONG,
  CE_ENGAGEMENT_DATE_IN_FUTURE,
  CE_ENGAGEMENT_END_BEFORE_START,
  CE_INVALID_CENTS,
  CE_INVALID_DOLLARS,
  CE_INVALID_NUMBER,
  CE_MAX_COMMENT,
  CE_MAX_DOLLARS,
  CE_MAX_HOURS_PER_DAY,
  CE_MIN_DOLLARS,
  getCeInvalidDuration,
} from '~/ui/constants/communityEngagement';
import { requiredField } from '~/ui/constants/errorMessages';

import { getOutOfMonthMessage, IEntryDateWindow, isInEntryDateWindow } from './entryDateWindow';

export interface IEntryFormValues {
  dateRange: DateRangeValue;
  minutesWorked: string;
  currencyEarned: string;
  comment: string;
}

export const getDefaultValues = (entry: ICeEntry | null): IEntryFormValues =>
  entry
    ? {
        dateRange: [parseISO(entry.startDate), parseISO(entry.endDate)],
        minutesWorked: entry.minutesWorked === null ? '' : minutesToDuration(entry.minutesWorked),
        currencyEarned: entry.currencyEarned === null ? '' : String(entry.currencyEarned),
        comment: entry.comment ?? '',
      }
    : {
        dateRange: [null, null],
        minutesWorked: '',
        currencyEarned: '',
        comment: '',
      };

const hasAtMostTwoDecimals = (value: number) =>
  Math.abs(value * 100 - Math.round(value * 100)) < 1e-9;

const dollars = string().test('dollars', (value, { createError }) => {
  if (!value?.trim()) return true;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return createError({ message: CE_INVALID_NUMBER });
  if (parsed < CE_MIN_DOLLARS || parsed > CE_MAX_DOLLARS) {
    return createError({ message: CE_INVALID_DOLLARS });
  }
  if (!hasAtMostTwoDecimals(parsed)) return createError({ message: CE_INVALID_CENTS });
  return true;
});

export const toAmount = (value: string): number | null =>
  value.trim() === '' ? null : Number(value);

export const getEntryMaxHours = ([start, end]: DateRangeValue): number =>
  start && end
    ? Math.max(differenceInCalendarDays(end, start) + 1, 1) * CE_MAX_HOURS_PER_DAY
    : CE_MAX_HOURS_PER_DAY;

export const toEntryDates = ([start, end]: DateRangeValue) => {
  const startDate = start ? formatDay(start) : '';
  return { startDate, endDate: end ? formatDay(end) : startDate };
};

export const isEntryFormChanged = (values: IEntryFormValues, defaults: IEntryFormValues) =>
  !isEqual(
    { ...values, dateRange: toEntryDates(values.dateRange) },
    { ...defaults, dateRange: toEntryDates(defaults.dateRange) },
  );

const getSchema = (selected: IYearMonth, window: IEntryDateWindow) =>
  object().shape({
    dateRange: mixed<DateRangeValue>().test('in-window', (range, { createError }) => {
      const days = (range ?? []).flatMap(date => (date ? [formatDay(date)] : []));
      if (!days.length) return createError({ message: requiredField });
      if (days.length === 2 && days[1] < days[0]) {
        return createError({ message: CE_ENGAGEMENT_END_BEFORE_START });
      }
      if (days.some(day => day > formatDay(new Date()))) {
        return createError({ message: CE_ENGAGEMENT_DATE_IN_FUTURE });
      }
      if (!days.every(day => isInEntryDateWindow(day, window))) {
        return createError({ message: getOutOfMonthMessage(selected) });
      }
      return true;
    }),
    minutesWorked: string().test('ce-duration', (value, { parent, createError }) => {
      const maxHours = getEntryMaxHours(parent.dateRange);
      return (
        isCeDuration(value, maxHours) || createError({ message: getCeInvalidDuration(maxHours) })
      );
    }),
    currencyEarned: dollars,
    comment: string().trim().max(CE_MAX_COMMENT, CE_COMMENT_TOO_LONG),
  });

export const getEntryResolver = (selected: IYearMonth, window: IEntryDateWindow) =>
  yupResolver(getSchema(selected, window));
